import { FinanceProgressSchema, FinanceStreamErrorSchema, FinanceStreamResultSchema, type FinanceProgress } from "@chro/shared";
import { ApiError, selectedCompanyId, type ApiResponseSchema } from "./api";

/** Metadata is bounded independently of the final, potentially large report.
 * The result header is followed by one complete JSON report through EOF. */
export async function readFinanceStream<T>(response: Response, schema: ApiResponseSchema<T>, onProgress: (value: FinanceProgress) => void): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(response.status, body?.error || response.statusText, body);
  }
  // Allows rolling release compatibility and existing JSON consumers/fixtures.
  if (!response.headers.get("content-type")?.includes("application/x-ndjson")) return schema.parse(await response.json());
  if (!response.body) throw new Error("The report connection returned no data. Please retry.");
  const reader = response.body.getReader(), decoder = new TextDecoder("utf-8", { fatal: true });
  let metadata = "", result = false, sequence = -1, resultLength = 0;
  const chunks: string[] = [];
  const consume = (text: string) => {
    while (text) {
      if (result) {
        resultLength += text.length;
        if (resultLength > 256 * 1024 * 1024) throw new Error("The report is too large. Select a shorter range.");
        chunks.push(text); return;
      }
      const newline = text.indexOf("\n");
      const part = newline === -1 ? text : text.slice(0, newline);
      if (metadata.length + part.length > 8192) throw new Error("Invalid report progress response.");
      metadata += part;
      if (newline === -1) return;
      const frame: unknown = JSON.parse(metadata); metadata = "";
      const progress = FinanceProgressSchema.safeParse(frame);
      if (progress.success) {
        if (progress.data.sequence <= sequence) throw new Error("Report progress arrived out of order.");
        sequence = progress.data.sequence; onProgress(progress.data);
      } else if (FinanceStreamResultSchema.safeParse(frame).success) result = true;
      else {
        const failure = FinanceStreamErrorSchema.safeParse(frame);
        throw new Error(failure.success ? failure.data.error : "Invalid report progress response.");
      }
      text = text.slice(newline + 1);
    }
  };
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      consume(decoder.decode(value, { stream: true }));
    }
    consume(decoder.decode());
    if (!result || !chunks.length) throw new Error("The report connection ended before completion. Please retry.");
    return schema.parse(JSON.parse(chunks.join("")));
  } catch (error) {
    await reader.cancel().catch(() => {});
    if (error instanceof TypeError || error instanceof SyntaxError) throw new Error("The report connection ended before completion. Please retry.");
    throw error;
  } finally { reader.releaseLock(); }
}

export async function apiContractStream<T>(schema: ApiResponseSchema<T>, path: string,
  onProgress: (value: FinanceProgress) => void, init: RequestInit = {}): Promise<T> {
  const company = selectedCompanyId(), headers = new Headers(init.headers);
  if (company && !headers.has("x-company-id")) headers.set("x-company-id", company);
  headers.set("accept", "application/x-ndjson");
  const response = await fetch(`/api${path}`, { credentials: "include", ...init, headers });
  return readFinanceStream(response, schema, value => { if (!init.signal?.aborted) onProgress(value); });
}
